// TMOS Related Actions Widget
// Date: December 9, 2025
// Purpose: Widget to display actions related to an entity (Risk, Issue, Project, etc.)
//
// Features:
// - Shows all actions linked to the current entity
// - Quick action creation from entity page
// - Displays action status and priority
// - Links to action detail page

import React, { useState, useEffect } from 'react';
import { actionService, Action } from '@/services/actionService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { CheckCircleIcon, ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { CreateActionModal } from './CreateActionModal';

interface RelatedActionsWidgetProps {
  contextType: string; // 'risk', 'issue', 'project', etc. (lowercase)
  contextId: string; // Entity ID
  title?: string; // Optional custom title
}

/**
 * RelatedActionsWidget Component
 * 
 * Displays actions related to a specific entity context.
 * Shows on Risk/Issue/KPI/Project detail pages.
 * 
 * Business Rules:
 * - Only shows actions user has permission to see (hierarchy filtering)
 * - Actions are filtered by context_type and context_id
 * - User can create new action from this widget (auto-associates with context)
 */
export const RelatedActionsWidget: React.FC<RelatedActionsWidgetProps> = ({
  contextType,
  contextId,
  title
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Load actions for this context
  useEffect(() => {
    loadActions();
  }, [contextType, contextId]);

  /**
   * Load actions for the context entity
   */
  const loadActions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await actionService.getActionsByContext(contextType, contextId);
      setActions(data);
    } catch (err: any) {
      console.error('Error loading related actions:', err);
      setError(err.message || 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle create action click
   * Opens modal with context pre-filled
   */
  const handleCreateAction = () => {
    setIsCreateModalOpen(true);
  };

  /**
   * Handle action creation success
   * Reloads the actions list
   */
  const handleActionCreated = () => {
    loadActions();
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-4 h-4 text-[var(--color-success)]" />;
      case 'IN_PROGRESS':
        return <ClockIcon className="w-4 h-4 text-[var(--color-info)]" />;
      default:
        return <ClockIcon className="w-4 h-4 text-[var(--color-secondary-text)] opacity-60" />;
    }
  };

  /**
   * Get priority badge color
   */
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return 'bg-[var(--color-error)]/20 text-[var(--color-error)]';
      case 'HIGH':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]';
      case 'MEDIUM':
        return 'bg-[var(--color-warning)]/20 text-[var(--color-warning)]';
      default:
        return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[var(--color-border)] p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
          {title || t('actions.relatedActions') || 'Related Actions'}
        </h3>
        <button
          onClick={handleCreateAction}
          className="flex items-center gap-1 px-3 py-1 bg-[var(--color-primary-bg)] text-[var(--color-secondary-font)] rounded text-xs hover:opacity-90"
        >
          <PlusIcon className="w-4 h-4" />
          {t('actions.createAction') || 'Create Action'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-2 bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded text-[var(--color-error)] text-xs">
          {error}
        </div>
      )}

      {/* Actions List */}
      {loading ? (
        <div className="text-center py-4 text-[var(--color-secondary-text)] text-sm">
          {t('actions.loading') || 'Loading...'}
        </div>
      ) : actions.length === 0 ? (
        <div className="text-center py-4 text-[var(--color-secondary-text)] text-sm">
          {t('actions.noRelatedActions') || 'No related actions'}
        </div>
      ) : (
        <div className="space-y-2">
          {actions.map((action) => (
            <div
              key={action.id}
              className="p-3 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-secondary-bg)] cursor-pointer"
              onClick={() => navigate(`/actions/${action.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusIcon(action.status)}
                    <span className="font-medium text-sm text-[var(--color-surface-text)]">
                      {action.title}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(action.priority)}`}>
                      {action.priority}
                    </span>
                  </div>
                  {action.due_date && (
                    <div className="text-xs text-[var(--color-secondary-text)]">
                      {t('actions.dueDate') || 'Due'}: {new Date(action.due_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Action Modal with Context Pre-filled */}
      <CreateActionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleActionCreated}
        contextType={contextType} // Pass as-is (backend handles case conversion)
        contextId={contextId}
      />
    </div>
  );
};

